<script>
  // A modal dialog shell (the want page's card options and New project
  // modal sit in it; a menu is shared/ContextMenu). It renders at the end of <body> over a
  // backdrop (the page's stacking layer would otherwise keep the app's fixed
  // menu button above it), with a title, a close button and the caller's
  // content. Focus moves in on open (to the element marked
  // `data-autofocus`, else the content's first control, else Done, never a
  // Remove an Enter could fire by accident) and goes back to
  // whatever had it (the tile or card) on close. Escape, the close button
  // and the backdrop all close it.
  import { portal } from '../../lib/actions/portal.js';

  /**
   * `title` is the heading's text, or a snippet for a heading with more in
   * it. `foot` is the dialog's action row:
   * a destructive `.remove` text button on the left, the `.done` button on
   * the right (both styled here).
   * @type {{
   *   title: string | import('svelte').Snippet,
   *   width?: number,
   *   onClose: () => void,
   *   children?: import('svelte').Snippet,
   *   foot?: import('svelte').Snippet,
   * }}
   */
  let { title, width = 420, onClose, children, foot } = $props();

  const titleId = `dialog-${Math.random().toString(36).slice(2, 8)}`;

  /**
   * After mount (the portal moves the backdrop into <body> after this runs,
   * and a detached element cannot take focus).
   * @param {HTMLElement} node
   */
  function holdFocus(node) {
    const before = /** @type {HTMLElement | null} */ (document.activeElement);
    queueMicrotask(() => {
      const controls = 'button, a[href], input, select, textarea';
      const target =
        node.querySelector('[data-autofocus]') ??
        node.querySelector(`.body :is(${controls})`) ??
        node.querySelector('.foot .done') ??
        node.querySelector('button');
      /** @type {HTMLElement | null} */ (target)?.focus();
    });
    return {
      destroy() {
        if (before?.isConnected) before.focus();
      },
    };
  }

  /** @param {KeyboardEvent} e */
  function onKeydown(e) {
    if (e.key === 'Escape') onClose();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<div class="backdrop" use:portal onclick={(e) => e.target === e.currentTarget && onClose()}>
  <div
    class="dialog"
    role="dialog"
    aria-modal="true"
    aria-labelledby={titleId}
    style:max-width="{width}px"
    use:holdFocus
  >
    <div class="head">
      <h2 id={titleId}>
        {#if typeof title === 'function'}{@render title()}{:else}{title}{/if}
      </h2>
      <button class="close" aria-label="Close" onclick={onClose}>&times;</button>
    </div>
    <div class="body">
      {@render children?.()}
    </div>
    {#if foot}
      <div class="foot">{@render foot()}</div>
    {/if}
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(44, 36, 25, 0.45);
  }
  .dialog {
    width: 100%;
    border-radius: var(--radius-lg);
    background: var(--color-bg-surface);
    box-shadow: 0 20px 60px rgba(44, 36, 25, 0.25);
  }
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 16px 20px 6px;
  }
  h2 {
    margin: 0;
    font-size: 1.125rem;
    color: var(--color-text);
  }
  .close {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border: none;
    border-radius: 8px;
    background: none;
    font-size: 24px;
    line-height: 1;
    color: var(--color-text-tertiary);
    cursor: pointer;
  }
  .close:active {
    background: var(--color-accent-bg);
  }
  .foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 20px 18px;
  }
  .foot :global(.remove) {
    padding: 9px 4px;
    border: none;
    background: none;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-error);
    cursor: pointer;
  }
  .foot :global(.done) {
    margin-left: auto;
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    background: var(--color-accent-dark);
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    cursor: pointer;
  }
  .foot :global(.done:active) {
    transform: scale(0.98);
  }
</style>
